/**
 * ProShip Direct — "does this merchant have a LIVE subscription?", for code outside this module.
 *
 * TWO DIFFERENT QUESTIONS, and they are not interchangeable:
 *   - MAY they use Direct at all?  → MerchantConfig.isProzoDirectEnabled, handled declaratively
 *     by `enableForConfig` on the routes. That hides the whole PROSHIP DIRECT group.
 *   - HAVE they bought a plan?     → this hook. It gates the one entry that is meaningless
 *     without a subscription (Direct Performance: every series on it is zero until Direct is
 *     actually allocating).
 *
 * ITS OWN FILE, deliberately — Sidebar imports it, and a React hook that fetches does not belong
 * in flags.js, which is dependency-free constants anything may import.
 *
 * CALLED ON EVERY SIDEBAR RENDER, so the fetch is shared: one module-level promise, resolved
 * once per page load, with every subscriber reading the same answer. Without that, an expanding
 * menu would re-request the subscription on each re-render.
 *
 * FAILS CLOSED. A 400 (Direct not enabled for this merchant), a network error or a merchant with
 * no subscription all resolve to false — the nav entry stays hidden rather than offering a page
 * that cannot load.
 */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchSubscription } from './data/api';
import { isDirectPlanEnabled } from './flags';

/** Statuses in which the merchant holds a usable plan. GRACE counts — they can still ship. */
const LIVE = ['ACTIVE', 'GRACE'];

let cached = null;          // Promise<boolean>, shared by every subscriber on this page load
const subscribers = new Set();

function resolveOnce() {
  if (!cached) {
    cached = fetchSubscription()
      .then((res) => LIVE.includes(res?.data?.status))
      .catch(() => false);
  }
  return cached;
}

/** Call after a purchase so the nav picks the new entry up without a reload. */
export function refreshDirectPlanActive() {
  cached = null;
  resolveOnce().then((v) => subscribers.forEach((fn) => fn(v)));
}

export default function useDirectPlanActive() {
  /*
    The config flag is already in Redux from login (fetchReportData), so it costs nothing and
    short-circuits the fetch entirely for the overwhelming majority of merchants — the ones who
    are not on Direct at all.
  */
  const { orderConversionDetails } = useSelector((state) => state.report);
  const mayUseDirect = Boolean(orderConversionDetails?.isProzoDirectEnabled);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!isDirectPlanEnabled() || !mayUseDirect) {
      setActive(false);
      return undefined;
    }
    let alive = true;
    const sync = (v) => { if (alive) setActive(v); };
    subscribers.add(sync);
    resolveOnce().then(sync);
    return () => { alive = false; subscribers.delete(sync); };
  }, [mayUseDirect]);

  return active;
}
